import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { OneOSLoginShell } from "../components/OneOSLoginShell";
import { ENGINES } from "../config/engines";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";

const LoginPage = () => {
  const { login, ssoUnavailable } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (email, password) => {
    const result = await login(email, password);
    if (result.success) {
      navigate("/");
      return { success: true };
    }
    return { success: false, error: result.error };
  };

  // SSO unavailable — distinct error surface, NOT a login attempt state
  if (ssoUnavailable) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
        <div className="noise-overlay" />
        <div className="fixed inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(239,68,68,0.05),transparent_50%)]" />

        <Card className="w-full max-w-md bg-card/50 backdrop-blur-xl border-rose-500/30 relative z-10" data-testid="sso-unavailable-card">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-rose-500/20 rounded-2xl flex items-center justify-center border border-rose-500/30">
              <AlertTriangle className="w-8 h-8 text-rose-400" />
            </div>
            <div>
              <CardTitle className="text-2xl font-manrope font-bold text-rose-400">SSO Unavailable</CardTitle>
              <CardDescription className="text-muted-foreground mt-2">
                Control Room Identity Service is currently unavailable
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="p-4 rounded-lg bg-rose-500/10 border border-rose-500/20">
              <p className="text-sm text-rose-300">
                The authentication service is not responding. This could be due to:
              </p>
              <ul className="mt-2 text-sm text-muted-foreground list-disc list-inside space-y-1">
                <li>Service maintenance</li>
                <li>Network connectivity issues</li>
                <li>Temporary service outage</li>
              </ul>
            </div>

            <Button
              onClick={() => window.location.reload()}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white font-medium"
              data-testid="retry-button"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry Connection
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              If the issue persists, please contact your system administrator.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <OneOSLoginShell
      engineCode={ENGINES.accounts.code}
      engineFull={ENGINES.accounts.full}
      onSubmit={handleSubmit}
    />
  );
};

export default LoginPage;
